import React, { useState, useEffect, useCallback } from 'react';
import { eventRegistrationAPI } from '../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────
function csvEscape(v) {
  const s = String(v ?? '');
  if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

function downloadCsv(rows) {
  const headers = ['Registration ID', 'Name', 'Phone', 'Email', 'Company', 'City',
    'Bringing Guest', 'Guest Name', 'Confirmed', 'Checked In', 'Check-In Time', 'Registered At'];
  const lines = [headers.join(',')];
  for (const r of rows) {
    lines.push([
      r.registrationId, r.name, r.phone, r.email, r.company, r.city,
      r.hasGuest ? 'Yes' : 'No', r.guestName,
      r.confirmAttendance ? 'Yes' : 'No', r.checkedIn ? 'Yes' : 'No',
      r.checkInTime ? new Date(r.checkInTime).toLocaleString() : '',
      r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
    ].map(csvEscape).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'launch-event-registrations.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Shared event-details block ────────────────────────────────────────────────
function EventDetails() {
  return (
    <div style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.7 }}>
      <p style={{ margin: '0 0 10px' }}>
        <strong>Sunday 26 July 2026 — 2:00 PM</strong> (doors open 1:30 PM)<br/>
        DECADEZ Lounge &amp; Restaurant, Kirinya — Kalagoyi Stage, behind the Coca-Cola Depot
      </p>
      <p style={{ margin: '0 0 10px' }}>
        <strong>What to expect:</strong> a live demo of the app, a <strong>FREE 24-hour internet
        voucher</strong> for every guest, prizes, and how to earn <strong>5% forever</strong> on referrals.
      </p>
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13 }}>
        Signed, Ojeka Simon Peter<br/>Managing Director, DWI FINTECH (U) LIMITED
      </p>
    </div>
  );
}

// ── Main Component — public /launch page ────────────────────────────────────
export default function EventRegistration() {
  const [statusLoading, setStatusLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', company: '', city: '',
    hasGuest: false, guestName: '', confirmAttendance: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null); // { registration, duplicate }

  useEffect(() => {
    eventRegistrationAPI.getStatus()
      .then(r => setEnabled(r.enabled !== false))
      .catch(() => setEnabled(true))
      .finally(() => setStatusLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setErr('Full name and phone number are required.');
      return;
    }
    setSubmitting(true);
    setErr('');
    try {
      const r = await eventRegistrationAPI.register(form);
      setResult(r);
    } catch (e2) {
      setErr(e2.response?.data?.message || e2.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    page:  { maxWidth: 520, margin: '0 auto', minHeight: '100vh', background: 'var(--bg-page, #0a1628)' },
    hero:  { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)', padding: '32px 20px', color: '#fff', textAlign: 'center' },
    title: { fontSize: 22, fontWeight: 800, margin: '0 0 8px' },
    sub:   { fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: 0 },
    body:  { padding: '24px 20px' },
    card:  { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, marginBottom: 20 },
    label: { fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4, marginTop: 14 },
    input: { width: '100%', padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
             border: '1px solid var(--border)', background: 'var(--bg-page)',
             color: 'var(--text-primary)', fontSize: 14 },
    checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, fontSize: 14, color: 'var(--text-primary)' },
    btn:   { width: '100%', marginTop: 22, padding: '13px 0', borderRadius: 10,
             background: '#22d3ee', color: '#0f172a', fontWeight: 700, fontSize: 15,
             border: 'none', cursor: 'pointer' },
    btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
    errBox: { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 14 },
    successBox: { background: 'var(--bg-card)', border: '2px solid #22d3ee', borderRadius: 14, padding: 20, textAlign: 'center' },
    successTitle: { fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' },
    idBox: { fontFamily: 'monospace', fontSize: 20, fontWeight: 800, letterSpacing: '0.08em',
             color: '#22d3ee', margin: '10px 0 18px' },
  };

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <h1 style={styles.title}>Official Launch of the DWI FINTECH Web App</h1>
        <p style={styles.sub}>You're invited — RSVP below.</p>
      </div>

      <div style={styles.body}>
        {statusLoading ? (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</p>
        ) : result ? (
          <div style={styles.successBox}>
            <p style={styles.successTitle}>{result.duplicate ? "You're already registered!" : "You're registered!"}</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>Registration ID</p>
            <div style={styles.idBox}>{result.registration?.registrationId}</div>
            <EventDetails />
          </div>
        ) : !enabled ? (
          <div style={styles.card}>
            <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px' }}>Registration has closed</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
              Thanks for your interest — RSVPs for this event are no longer being accepted.
            </p>
          </div>
        ) : (
          <>
            <div style={styles.card}><EventDetails /></div>

            <form onSubmit={handleSubmit}>
              <label style={styles.label}>Full name *</label>
              <input style={styles.input} type="text" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />

              <label style={styles.label}>Phone *</label>
              <input style={styles.input} type="tel" placeholder="+256 7XX XXX XXX" value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />

              <label style={styles.label}>Email</label>
              <input style={styles.input} type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />

              <label style={styles.label}>Company</label>
              <input style={styles.input} type="text" value={form.company}
                onChange={e => setForm(f => ({ ...f, company: e.target.value }))} />

              <label style={styles.label}>City</label>
              <input style={styles.input} type="text" value={form.city}
                onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />

              <label style={styles.checkboxRow}>
                <input type="checkbox" checked={form.hasGuest}
                  onChange={e => setForm(f => ({ ...f, hasGuest: e.target.checked }))} />
                Bringing one guest?
              </label>

              {form.hasGuest && (
                <>
                  <label style={styles.label}>Guest name</label>
                  <input style={styles.input} type="text" value={form.guestName}
                    onChange={e => setForm(f => ({ ...f, guestName: e.target.value }))} />
                </>
              )}

              <label style={styles.checkboxRow}>
                <input type="checkbox" checked={form.confirmAttendance}
                  onChange={e => setForm(f => ({ ...f, confirmAttendance: e.target.checked }))} />
                I confirm I plan to attend
              </label>

              {err && <div style={styles.errBox}>{err}</div>}

              <button type="submit" style={{ ...styles.btn, ...(submitting ? styles.btnDisabled : {}) }} disabled={submitting}>
                {submitting ? 'Submitting…' : 'RSVP Now'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ── Admin tab — Launch Event ──────────────────────────────────────────────────
const AS = {
  input: { padding: '8px 12px', borderRadius: 7, border: '1px solid var(--border)',
           background: 'var(--bg-page)', color: 'var(--text-primary)', minWidth: 220 },
  btn: (disabled, color) => ({
    padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 12,
    border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? 'var(--border)' : (color || '#22d3ee'),
    color: disabled ? 'var(--text-muted)' : (color === '#dc2626' || color === '#16a34a' ? '#fff' : '#0f172a'),
    opacity: disabled ? 0.6 : 1,
  }),
  stat: (color) => ({
    display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
    background: color + '22', borderRadius: 8, padding: '8px 18px', minWidth: 90,
    border: '1px solid ' + color + '44',
  }),
  row: { background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 10,
         padding: '10px 14px', marginBottom: 8 },
  pill: (bg, color) => ({
    display: 'inline-block', background: bg, color, padding: '2px 10px', borderRadius: 99,
    fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginLeft: 6,
  }),
};

export function LaunchEventAdminTab() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [counts, setCounts] = useState({ registrations: 0, guests: 0, expectedTotal: 0 });
  const [togglingId, setTogglingId] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, countsRes] = await Promise.all([
        eventRegistrationAPI.adminList({ search: search || undefined, limit: 1000 }),
        eventRegistrationAPI.adminCounts(),
      ]);
      setRegistrations(listRes.docs || []);
      setCounts(countsRes);
    } catch (e) {
      setMsg(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const toggleCheckIn = async (r) => {
    setTogglingId(r._id);
    try {
      if (r.checkedIn) await eventRegistrationAPI.undoCheckIn(r._id);
      else await eventRegistrationAPI.checkIn(r._id);
      await load();
    } catch (e) {
      setMsg(e.response?.data?.message || e.message);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <h3 style={{ marginTop: 0, marginBottom: 16 }}>Launch Event — Registrations</h3>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={AS.stat('#22d3ee')}>
          <span style={{ fontSize: 20, fontWeight: 800, color: '#22d3ee' }}>{counts.registrations}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#22d3ee', textTransform: 'uppercase' }}>Registrations</span>
        </div>
        <div style={AS.stat('#a855f7')}>
          <span style={{ fontSize: 20, fontWeight: 800, color: '#a855f7' }}>{counts.guests}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#a855f7', textTransform: 'uppercase' }}>Guests</span>
        </div>
        <div style={AS.stat('#16a34a')}>
          <span style={{ fontSize: 20, fontWeight: 800, color: '#16a34a' }}>{counts.expectedTotal}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase' }}>Expected Total</span>
        </div>
      </div>

      {msg && <p style={{ color: '#dc2626', fontSize: 13 }}>{msg}</p>}

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          style={AS.input}
          type="text"
          placeholder="Search name, phone, company, ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button style={AS.btn(registrations.length === 0)} disabled={registrations.length === 0}
          onClick={() => downloadCsv(registrations)}>
          Export CSV
        </button>
      </div>

      {loading ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> :
      registrations.length === 0 ? <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No registrations yet.</p> :
      registrations.map(r => (
        <div key={r._id} style={AS.row}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <strong>{r.name}</strong>
              {r.checkedIn
                ? <span style={AS.pill('#dcfce7', '#166534')}>CHECKED IN</span>
                : <span style={AS.pill('#fef9c3', '#854d0e')}>NOT ARRIVED</span>}
              {r.hasGuest && <span style={AS.pill('#e0e7ff', '#3730a3')}>+1 GUEST</span>}
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>
                {r.phone} {r.company ? '· ' + r.company : ''} {r.city ? '· ' + r.city : ''}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'monospace' }}>
                {r.registrationId}
              </div>
            </div>
            <button
              style={AS.btn(togglingId === r._id, r.checkedIn ? '#dc2626' : '#16a34a')}
              disabled={togglingId === r._id}
              onClick={() => toggleCheckIn(r)}
            >
              {togglingId === r._id ? '…' : r.checkedIn ? 'Undo' : 'Check In'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
